import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * useAvatar — backend-backed avatar metadata for the signed-in user.
 *
 * All requests are credentialed; the backend identifies the user from the
 * httpOnly session cookie. If the user is not signed in (or hasn't paid),
 * the hook returns avatar=null and surfaces clear errors when the user
 * tries to upload.
 */
export function useAvatar() {
  const { user, loading: authLoading } = useAuth();
  const userId = user ? user.user_id : null;

  const [avatar, setAvatar] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | uploading | error
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const abortRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!API || !user) {
      setAvatar(null);
      setStatus("idle");
      return;
    }
    setStatus("loading");
    try {
      const r = await fetch(`${API}/api/avatars/me`, {
        credentials: "include",
      });
      if (r.status === 401) {
        setAvatar(null);
        setStatus("idle");
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setAvatar(data.avatar || null);
      setStatus("idle");
    } catch (e) {
      setStatus("error");
      setError(String(e));
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  const upload = useCallback(
    async (file) => {
      if (!API) return null;
      if (!user) {
        setError("Sign in first to upload an avatar.");
        setStatus("error");
        return null;
      }
      if (!user.hasPaid) {
        setError("Custom Avatar Pass required.");
        setStatus("error");
        return null;
      }

      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (!["glb", "gltf", "usdz"].includes(ext)) {
        setError("Only .glb, .gltf, .usdz files are supported.");
        setStatus("error");
        return null;
      }
      setStatus("uploading");
      setError(null);
      setUploadProgress(0);

      const url = `${API}/api/avatars/upload`;
      const form = new FormData();
      form.append("file", file);

      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        abortRef.current = xhr;
        xhr.open("POST", url);
        xhr.withCredentials = true; // send the session cookie
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          abortRef.current = null;
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              setAvatar(data);
              setStatus("idle");
              setUploadProgress(100);
              resolve(data);
            } catch (e) {
              setStatus("error");
              setError("Bad response");
              resolve(null);
            }
          } else {
            setStatus("error");
            try {
              const j = JSON.parse(xhr.responseText);
              setError(j.detail || `HTTP ${xhr.status}`);
            } catch {
              setError(`HTTP ${xhr.status}`);
            }
            resolve(null);
          }
        };
        xhr.onerror = () => {
          abortRef.current = null;
          setStatus("error");
          setError("Network error");
          resolve(null);
        };
        xhr.send(form);
      });
    },
    [user]
  );

  const clear = useCallback(async () => {
    if (!API || !user) return;
    setStatus("loading");
    try {
      await fetch(`${API}/api/avatars/me`, {
        method: "DELETE",
        credentials: "include",
      });
      setAvatar(null);
      setStatus("idle");
    } catch (e) {
      setStatus("error");
      setError(String(e));
    }
  }, [user]);

  const avatarUrl = avatar
    ? `${API}/api/avatars/file/${encodeURIComponent(avatar.id)}`
    : null;

  const isRenderableInScene =
    !!avatar && (avatar.ext === "glb" || avatar.ext === "gltf");

  return {
    userId,
    avatar,
    avatarUrl,
    isRenderableInScene,
    status,
    error,
    uploadProgress,
    upload,
    clear,
    refresh,
  };
}
